import { useContext } from 'react'
import { FieldValues } from 'react-hook-form'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'

export const useMapDataToFormValues = () => {
	const storeCtx = useContext(StoreSettingsContext)
	const { isAdmin, isSuperuser } = useUserRoles()

	const mapData = (data: FieldValues) => {
		const logo = data.logo ?? []

		const formData = new FormData()
		formData.append('address.address', data.address.address)
		formData.append('address.postal_code', data.address.postal_code)
		formData.append('address.city', data.address?.city)
		formData.append('address.country', data.country?.value)

		formData.append('legal_address.address', data.legal_address.address)
		formData.append('legal_address.postal_code', data.legal_address.postal_code)
		formData.append('legal_address.city', data.legal_address?.city)
		formData.append('legal_address.country', data.legal_country?.value)

		formData.append('name', data.name)
		formData.append('payment_cancel_url', data.payment_cancel_url)
		formData.append('payment_receipt_url', data.payment_receipt_url)
		formData.append('currency', data.currency?.value)

		formData.append('phone', data.phone)
		formData.append('pinterest_url', data.pinterest_url)
		formData.append('rapyd_access_key', data.rapyd_access_key)
		formData.append('rapyd_secret_key', data.rapyd_secret_key)
		formData.append('rapyd_sandbox', data.rapyd_sandbox)
		formData.append('twitter_url', data.twitter_url)
		formData.append('website_url', data.website_url)
		formData.append('default_from_email', data.default_from_email)
		formData.append('email', data.email)
		formData.append('facebook_url', data.facebook_url)
		formData.append('instagram_url', data.instagram_url)
		formData.append('legal_id', data.legal_id)
		formData.append('legal_name', data.legal_name)
		formData.append(
			'terms_of_service_url',
			(isAdmin || isSuperuser
				? data.terms_of_service_url
				: storeCtx?.storeSettings.terms_of_service_url) ?? ''
		)
		formData.append('linkedin_url', data.linkedin_url)
		formData.append('quantity_enabled', data.quantity_enabled)
		formData.append('cost_price_enabled', data.cost_price_enabled)
		formData.append('categories_enabled', data.categories_enabled)
		formData.append('photos_required_to_publish', data.photos_required_to_publish)
		formData.append('tags_enabled', data.tags_enabled)
		formData.append('color_enabled', data.color_enabled)
		formData.append('users_can_publish_products', data.users_can_publish_products)
		formData.append('default_product_publish_value', data.default_product_publish_value)
		formData.append('coupons_enabled', data.coupons_enabled)
		if (Array.isArray(data.enabled_payout_methods)) {
			data.enabled_payout_methods.forEach((code: string) =>
				formData.append('enabled_payout_methods', code)
			)
		}
		formData.append('product_conditions_enabled', data.product_conditions_enabled)
		formData.append('product_fabrics_enabled', data.product_fabrics_enabled)
		formData.append('product_genders_enabled', data.product_genders_enabled)
		formData.append('supplier_sku_barcode_enabled', data.supplier_sku_barcode_enabled)
		formData.append('vat_enabled', data.vat_enabled)
		formData.append('product_confirm_enabled', data.product_confirm_enabled)
		formData.append('product_types_enabled', data.product_types_enabled)
		formData.append(
			'booking_fees.product_storage_max_days',
			data.booking_fees.product_storage_max_days
		)
		formData.append('booking_fees.booth_takedown_fee', data.booking_fees.booth_takedown_fee)
		formData.append(
			'booking_fees.booth_takedown_fee_notified',
			data.booking_fees.booth_takedown_fee_notified
		)
		formData.append(
			'booking_fees.product_storage_fee_per_day',
			data.booking_fees.product_storage_fee_per_day
		)
		if (isSuperuser) {
			formData.append('netgiro_app_id', data.netgiro_app_id)
			formData.append('netgiro_secret_key', data.netgiro_secret_key)
			formData.append('netgiro_sandbox', data.netgiro_sandbox)

			formData.append('resalex_client_id', data.resalex_client_id)
			formData.append('resalex_client_secret', data.resalex_client_secret)
			formData.append('resalex_sandbox', data.resalex_sandbox)

			formData.append('sendgrid_end_reminder_template_id', data.sendgrid_end_reminder_template_id)
			formData.append(
				'sendgrid_start_reminder_template_id',
				data.sendgrid_start_reminder_template_id
			)
			formData.append('sendgrid_receipt_template_id', data.sendgrid_receipt_template_id)
			formData.append('sendgrid_welcome_template_id', data.sendgrid_welcome_template_id)
			formData.append(
				'sendgrid_payout_method_reminder_template_id',
				data.sendgrid_payout_method_reminder_template_id
			)
			formData.append('start_reminder_sms_template', data.start_reminder_sms_template)
			formData.append('end_reminder_sms_template', data.end_reminder_sms_template)
		} else {
			formData.append(
				'sendgrid_end_reminder_template_id',
				storeCtx?.storeSettings.sendgrid_end_reminder_template_id
			)
			formData.append(
				'sendgrid_start_reminder_template_id',
				storeCtx?.storeSettings.sendgrid_start_reminder_template_id
			)
			formData.append(
				'sendgrid_receipt_template_id',
				storeCtx?.storeSettings.sendgrid_receipt_template_id
			)
			formData.append(
				'sendgrid_welcome_template_id',
				storeCtx?.storeSettings.sendgrid_welcome_template_id
			)
			formData.append(
				'sendgrid_payout_method_reminder_template_id',
				storeCtx?.storeSettings.sendgrid_payout_method_reminder_template_id
			)
			formData.append(
				'start_reminder_sms_template',
				storeCtx?.storeSettings.start_reminder_sms_template
			)
			formData.append(
				'end_reminder_sms_template',
				storeCtx?.storeSettings.end_reminder_sms_template
			)
		}
		formData.append('default_language', data.default_language.value)

		formData.append('logo', logo)

		return formData
	}

	return { mapData }
}
